/**
 * Sound effects manager for the flashcard app
 * Uses Web Audio API to generate sounds without external files
 */

let audioContext = null;
let soundEnabled = true;

// Initialize audio context (must be called after user interaction)
const initAudio = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Set sound enabled state
export const setSoundEnabled = (enabled) => {
  soundEnabled = enabled;
  localStorage.setItem('soundEnabled', JSON.stringify(enabled));
};

// Get sound enabled state
export const getSoundEnabled = () => {
  const stored = localStorage.getItem('soundEnabled');
  if (stored !== null) {
    soundEnabled = JSON.parse(stored);
  }
  return soundEnabled;
};

// Play a tone with given frequency and duration
const playTone = (frequency, duration, type = 'sine', volume = 0.3) => {
  if (!soundEnabled) return;

  try {
    const ctx = initAudio();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

    // Fade in and out for smoother sound
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  } catch (e) {
    console.warn('Sound playback failed:', e);
  }
};

// Play multiple tones in sequence (for melodies)
const playMelody = (notes, baseVolume = 0.3) => {
  if (!soundEnabled) return;

  try {
    const ctx = initAudio();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    let time = ctx.currentTime;

    notes.forEach(({ freq, duration, type = 'sine', volume = baseVolume }) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(freq, time);

      gainNode.gain.setValueAtTime(0, time);
      gainNode.gain.linearRampToValueAtTime(volume, time + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, time + duration - 0.01);

      oscillator.start(time);
      oscillator.stop(time + duration);

      time += duration * 0.8; // Slight overlap for smoother melody
    });
  } catch (e) {
    console.warn('Sound playback failed:', e);
  }
};

// Sound effects
export const sounds = {
  // Card flip - subtle click
  flip: () => {
    playTone(800, 0.05, 'square', 0.1);
  },

  // Correct answer (Good/Easy) - pleasant chime
  correct: () => {
    playMelody([
      { freq: 523, duration: 0.1 },  // C5
      { freq: 659, duration: 0.15 }  // E5
    ], 0.25);
  },

  // Easy answer - extra pleasant
  easy: () => {
    playMelody([
      { freq: 523, duration: 0.08 },  // C5
      { freq: 659, duration: 0.08 },  // E5
      { freq: 784, duration: 0.15 }   // G5
    ], 0.25);
  },

  // Wrong answer (Again) - soft low tone
  wrong: () => {
    playTone(220, 0.2, 'sine', 0.2);
  },

  // Hard answer - neutral tone
  hard: () => {
    playTone(330, 0.15, 'triangle', 0.15);
  },

  // Achievement unlocked - celebration fanfare
  achievement: () => {
    playMelody([
      { freq: 523, duration: 0.1 },   // C5
      { freq: 659, duration: 0.1 },   // E5
      { freq: 784, duration: 0.1 },   // G5
      { freq: 1047, duration: 0.3 }   // C6
    ], 0.3);
  },

  // Level up - triumphant melody
  levelUp: () => {
    playMelody([
      { freq: 392, duration: 0.1 },   // G4
      { freq: 523, duration: 0.1 },   // C5
      { freq: 659, duration: 0.1 },   // E5
      { freq: 784, duration: 0.15 },  // G5
      { freq: 1047, duration: 0.25 }  // C6
    ], 0.35);
  },

  // Daily goal complete - victory sound
  goalComplete: () => {
    playMelody([
      { freq: 523, duration: 0.1 },   // C5
      { freq: 587, duration: 0.1 },   // D5
      { freq: 659, duration: 0.1 },   // E5
      { freq: 784, duration: 0.2 },   // G5
      { freq: 659, duration: 0.1 },   // E5
      { freq: 784, duration: 0.3 }    // G5
    ], 0.3);
  },

  // Streak milestone - special celebratory sound
  streak: () => {
    playMelody([
      { freq: 392, duration: 0.08 },  // G4
      { freq: 440, duration: 0.08 },  // A4
      { freq: 523, duration: 0.08 },  // C5
      { freq: 659, duration: 0.12 },  // E5
      { freq: 784, duration: 0.2 }    // G5
    ], 0.3);
  },

  // Challenge complete - reward sound
  challengeComplete: () => {
    playMelody([
      { freq: 659, duration: 0.1 },   // E5
      { freq: 784, duration: 0.1 },   // G5
      { freq: 880, duration: 0.1 },   // A5
      { freq: 1047, duration: 0.25 }  // C6
    ], 0.35);
  },

  // Button click - subtle feedback
  click: () => {
    playTone(600, 0.03, 'square', 0.08);
  },

  // Notification - gentle alert
  notification: () => {
    playMelody([
      { freq: 880, duration: 0.1 },   // A5
      { freq: 660, duration: 0.15 }   // E5
    ], 0.2);
  }
};

// Play sound by name
export const playSound = (soundName) => {
  if (sounds[soundName]) {
    sounds[soundName]();
  }
};

export default sounds;
